import React, { useMemo } from 'react';

import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { ThemeIcon } from 'src/design/atoms/ThemeIcon';
import { Accordion } from 'src/design/molecules/Accordion';
import { IconThumbDown, IconThumbUp, IconTilde } from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import {
  UnitOfMeasureType,
  WbsTreeData,
} from 'src/routes/Jobs/WBS/components/types';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';
import { CTCLinkIndicator } from 'src/routes/Jobs/WBS/components/CTCLinkIndicator';
import { useWbs } from 'src/routes/Jobs/WBS/contexts/WbsContext';
import { convertAccountsToTreeData } from 'src/utils';

interface SimpleTrackerProps {
  jobId: number;
  jobNumber: string;
}

interface TrackerRowData {
  id: string;
  name: string;
  depth: number;
  isLeaf: boolean;
  estimatedHours: number;
  estimatedQuantity: number;
  estimatedRunRate: number;
  actualHours: number;
  actualQuantity: number;
  actualRunRate: number;
  hoursToGo: number;
  estimatedHoursToGo: number;
  variance: number;
  completionPercentageHours: number;
  completionPercentageQuantity: number;
  overallPerformance: number;
  unitOfMeasure?: UnitOfMeasureType;
  node: WbsTreeData;
  subRows?: TrackerRowData[];
}

const roundToTwo = (num: number) => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

// Helper function to format values based on unit of measure
const formatValue = (
  value: number,
  digits: number,
  unitOfMeasure?: UnitOfMeasureType,
) => {
  if (unitOfMeasure === '$') {
    return `${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      style: 'currency',
      currency: 'USD',
    })}`;
  } else if (unitOfMeasure === 'hours') {
    return `${value.toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })}`;
  } else {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  }
};

// Helper function to get color based on value and type
const getValueColor = (
  value: number,
  type: 'percentage' | 'variance' | 'rate' | 'default' | 'overall-performance',
) => {
  if (type === 'percentage') {
    if (value >= 90) return 'black';
    if (value >= 75) return 'yellow';
    if (value >= 50) return 'orange';
    return 'red';
  }
  if (type === 'variance') {
    if (value > 0) return 'black';
    if (value < 0) return 'red';
    return 'gray';
  }
  if (type === 'rate') {
    if (value > 0) return 'blue';
    return 'gray';
  }
  if (type === 'overall-performance') {
    if (value > 5) return 'black';
    if (value < -5) return 'red';
    return 'yellow';
  }
  return 'black';
};

// Calculate aggregated values for non-leaf nodes
const calculateAggregatedValues = (
  nodes: TrackerRowData[],
): {
  estimatedHours: number;
  estimatedQuantity: number;
  actualHours: number;
  actualQuantity: number;
  hoursToGo: number;
  estimatedHoursToGo: number;
  variance: number;
  estimatedRunRate: number;
  actualRunRate: number;
  completionPercentageHours: number;
  completionPercentageQuantity: number;
  overallPerformance: number;
} => {
  const leafNodes = nodes.filter((node) => node.isLeaf);

  if (leafNodes.length === 0) {
    return {
      estimatedHours: 0,
      estimatedQuantity: 0,
      actualHours: 0,
      actualQuantity: 0,
      hoursToGo: 0,
      estimatedHoursToGo: 0,
      variance: 0,
      estimatedRunRate: 0,
      actualRunRate: 0,
      completionPercentageHours: 0,
      completionPercentageQuantity: 0,
      overallPerformance: 0,
    };
  }

  // Sum values for aggregation
  const summed = leafNodes.reduce(
    (acc, node) => {
      acc.estimatedHours += node.estimatedHours;
      acc.estimatedQuantity += node.estimatedQuantity;
      acc.actualHours += node.actualHours;
      acc.actualQuantity += node.actualQuantity;
      acc.hoursToGo += node.hoursToGo;
      acc.estimatedHoursToGo += node.estimatedHoursToGo;
      acc.variance += node.variance;
      return acc;
    },
    {
      estimatedHours: 0,
      estimatedQuantity: 0,
      actualHours: 0,
      actualQuantity: 0,
      hoursToGo: 0,
      estimatedHoursToGo: 0,
      variance: 0,
    },
  );

  // Calculate rates and percentages (averaged)
  const estimatedRunRate =
    summed.estimatedQuantity > 0
      ? roundToTwo(summed.estimatedHours / summed.estimatedQuantity)
      : 0;

  const actualRunRate =
    summed.actualQuantity > 0
      ? roundToTwo(summed.actualHours / summed.actualQuantity)
      : 0;

  const completionPercentageQuantity =
    summed.estimatedQuantity > 0
      ? roundToTwo((summed.actualQuantity / summed.estimatedQuantity) * 100)
      : 0;

  const completionPercentageHours =
    summed.estimatedHours > 0
      ? roundToTwo((summed.actualHours / summed.estimatedHours) * 100)
      : 0;

  const overallPerformance =
    completionPercentageQuantity - completionPercentageHours;

  return {
    ...summed,
    estimatedRunRate,
    actualRunRate,
    completionPercentageHours,
    completionPercentageQuantity,
    overallPerformance,
  };
};

// Transform WBS tree data into flat structure with subRows for mantine-react-table
const transformWbsToTableData = (
  nodes: WbsTreeData[],
  depth = 0,
): TrackerRowData[] => {
  const rows: TrackerRowData[] = [];

  nodes.forEach((node) => {
    const shouldInclude =
      (node.active && node.apply && node.accountType == 'labor') ||
      (node.children && node.children.length > 0);
    if (!shouldInclude) return;

    const isLeaf = node.apply && (!node.children || node.children.length === 0);

    if (isLeaf) {
      // Calculate analytics for leaf nodes
      const estimatedQuantity = Number(node.currentQuantityEstimate);
      const actualQuantity = Number(node.currentQuantityActual);
      const estimatedHours = Number(node.currentHoursEstimate);
      const actualHours = Number(node.currentHoursActual);
      const remainingQuantity = estimatedQuantity - actualQuantity;

      const completionPercentageQuantity =
        estimatedQuantity > 0
          ? roundToTwo((actualQuantity / estimatedQuantity) * 100)
          : 0;

      const completionPercentageHours =
        estimatedHours > 0
          ? roundToTwo((actualHours / estimatedHours) * 100)
          : 0;

      const actualRunRate =
        actualQuantity > 0 ? roundToTwo(actualHours / actualQuantity) : 0;
      const estimatedRunRate =
        estimatedQuantity > 0
          ? roundToTwo(estimatedHours / estimatedQuantity)
          : 0;

      // If actual quantity is 0, use estimated hours to calculate hours to go
      const hoursToGo =
        actualQuantity > 0 ? remainingQuantity * actualRunRate : estimatedHours;
      const estimatedHoursToGo = actualHours + hoursToGo;
      const variance = estimatedHours - estimatedHoursToGo;
      const overallPerformance =
        completionPercentageQuantity - completionPercentageHours;

      rows.push({
        id: node.id,
        name: node.name,
        depth,
        isLeaf: true,
        estimatedHours,
        estimatedQuantity,
        estimatedRunRate,
        actualHours,
        actualQuantity,
        actualRunRate,
        hoursToGo,
        estimatedHoursToGo,
        variance,
        completionPercentageHours,
        completionPercentageQuantity,
        overallPerformance,
        unitOfMeasure: node.unitOfMeasure,
        node,
      });
    } else {
      // Non-leaf node (rollup) - calculate aggregated values
      const childRows = node.children
        ? transformWbsToTableData(node.children, depth + 1)
        : [];

      const aggregated = calculateAggregatedValues(childRows);

      rows.push({
        id: node.id,
        name: node.name,
        depth,
        isLeaf: false,
        estimatedHours: aggregated.estimatedHours,
        estimatedQuantity: aggregated.estimatedQuantity,
        estimatedRunRate: aggregated.estimatedRunRate,
        actualHours: aggregated.actualHours,
        actualQuantity: aggregated.actualQuantity,
        actualRunRate: aggregated.actualRunRate,
        hoursToGo: aggregated.hoursToGo,
        estimatedHoursToGo: aggregated.estimatedHoursToGo,
        variance: aggregated.variance,
        completionPercentageHours: aggregated.completionPercentageHours,
        completionPercentageQuantity: aggregated.completionPercentageQuantity,
        overallPerformance: aggregated.overallPerformance,
        unitOfMeasure: node.unitOfMeasure,
        node,
        subRows: childRows,
      });
    }
  });

  return rows;
};

// Legend component for explaining formulas and acronyms
const TrackerLegend: React.FC = () => {
  return (
    <Accordion variant="separated" mb="md">
      <Accordion.Item value="legend">
        <Accordion.Control>
          <Text fw={500}>Column Formulas & Definitions</Text>
        </Accordion.Control>
        <Accordion.Panel>
          <Stack>
            <Text size="sm">
              This table shows project tracking metrics with calculated values
              based on estimated vs actual performance.
            </Text>

            <Text size="sm" fw={600}>
              Column Definitions:
            </Text>
            <Stack component="ul" ml="md">
              <Text component="li">
                <Text component="span" c="black">
                  Est. Hrs:
                </Text>{' '}
                Estimated hours for the job
              </Text>
              <Text component="li">
                <Text component="span" c="black">
                  Est. Qty:
                </Text>{' '}
                Estimated quantity to be installed
              </Text>
              <Text component="li">
                <Text component="span" c="black">
                  Est. Factor:
                </Text>{' '}
                Estimated hours per unit. Formula: Est. Hrs ÷ Est. Qty
              </Text>
              <Text component="li">
                <Text component="span" c="black">
                  Actual Hrs:
                </Text>{' '}
                Total hours worked so far
              </Text>
              <Text component="li">
                <Text component="span" c="black">
                  Installed Qty:
                </Text>{' '}
                Total quantity installed so far
              </Text>
              <Text component="li">
                <Text component="span" c="black">
                  Productivity Rate:
                </Text>{' '}
                Actual hours per unit. Formula: Actual Hrs ÷ Installed Qty
              </Text>
              <Text component="li">
                <Text component="span" c="black">
                  HTG (Hours To Go):
                </Text>{' '}
                Remaining hours to complete. Formula: (Est. Qty - Installed Qty)
                × Productivity Rate
              </Text>
              <Text component="li">
                <Text component="span" c="black">
                  E@C (Estimate at Completion):
                </Text>{' '}
                Total hours at completion. Formula: Actual Hrs + HTG
              </Text>
              <Text component="li">
                <Text component="span" c="black">
                  Variance:
                </Text>{' '}
                Difference between original estimate and E@C. Formula: Est. Hrs
                - E@C
              </Text>
              <Text component="li">
                <Text component="span" c="black">
                  % Hrs Used:
                </Text>{' '}
                Percentage of estimated hours used. Formula: (Actual Hrs ÷ Est.
                Hrs) × 100
              </Text>
              <Text component="li">
                <Text component="span" c="black">
                  % Qty Installed:
                </Text>{' '}
                Percentage of estimated quantity installed. Formula: (Installed
                Qty ÷ Est. Qty) × 100
              </Text>
              <Text component="li">
                <Text component="span" c="black">
                  Overall Performance:
                </Text>{' '}
                Difference between % Qty Installed and % Hrs Used. If positive,
                the job is ahead of schedule. If negative, the job is behind
                schedule. Formula: % Qty Installed - % Hrs Used
              </Text>
            </Stack>
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

export const SimpleTracker: React.FC<SimpleTrackerProps> = ({
  jobId,
  jobNumber,
}) => {
  const { wbsData: wbsMaster, loading } = useWbs();

  const wbs = useMemo<WbsTreeData[]>(() => {
    if (!wbsMaster) return [];

    return convertAccountsToTreeData(
      wbsMaster.wbsAccount,
      (account) =>
        account.active &&
        !account.archived &&
        account.accountType === 'labor' &&
        !account.timesheetsOnly,
    );
  }, [wbsMaster]);

  // Transform WBS data for table
  const tableData = useMemo(() => {
    return transformWbsToTableData(wbs);
  }, [wbs]);

  // Column definitions
  const columns = useMemo<MRT_ColumnDef<TrackerRowData>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        Cell: ({ row }) => (
          <Group gap="xs" align="center">
            <Text c="black">{row.original.name}</Text>
            <CTCLinkIndicator node={row.original.node} size="xs" />
          </Group>
        ),
      },
      {
        id: 'estimatedHours',
        size: 150,
        accessorFn: (row) => row.estimatedHours,
        header: 'Est. Hrs',
        Cell: ({ cell, row }) => (
          <Text c={getValueColor(cell.getValue<number>(), 'default')}>
            {formatValue(cell.getValue<number>(), 0)}
          </Text>
        ),
      },
      {
        id: 'estimatedQuantity',
        size: 150,
        accessorFn: (row) => row.estimatedQuantity,
        header: 'Est. Qty',
        Cell: ({ cell, row }) => (
          <Text c={getValueColor(cell.getValue<number>(), 'default')}>
            {formatValue(cell.getValue<number>(), 0)}
          </Text>
        ),
      },
      {
        id: 'estimatedRunRate',
        size: 150,
        accessorFn: (row) => row.estimatedRunRate,
        header: 'Est. Factor',
        Cell: ({ cell, row }) => (
          <Text c={getValueColor(cell.getValue<number>(), 'rate')}>
            {formatValue(cell.getValue<number>(), 2)}
          </Text>
        ),
      },
      {
        id: 'actualHours',
        size: 150,
        accessorFn: (row) => row.actualHours,
        header: 'Actual Hrs',
        Cell: ({ cell, row }) => (
          <Text c={getValueColor(cell.getValue<number>(), 'default')}>
            {formatValue(cell.getValue<number>(), 0)}
          </Text>
        ),
      },
      {
        id: 'actualQuantity',
        size: 150,
        accessorFn: (row) => row.actualQuantity,
        header: 'Installed Qty',
        Cell: ({ cell, row }) => (
          <Text c={getValueColor(cell.getValue<number>(), 'default')}>
            {formatValue(cell.getValue<number>(), 0)}
          </Text>
        ),
      },
      {
        id: 'actualRunRate',
        size: 150,
        accessorFn: (row) => row.actualRunRate,
        header: 'Productivity Rate',
        Cell: ({ cell, row }) => (
          <Text c={getValueColor(cell.getValue<number>(), 'rate')}>
            {formatValue(cell.getValue<number>(), 2)}
          </Text>
        ),
      },
      {
        id: 'hoursToGo',
        size: 150,
        accessorFn: (row) => row.hoursToGo,
        header: 'HTG',
        mantineTableHeadCellProps: {
          style: {
            backgroundColor: '#64ffc0',
          },
        },
        Cell: ({ cell, row }) => (
          <Text c={getValueColor(cell.getValue<number>(), 'default')}>
            {formatValue(cell.getValue<number>(), 0)}
          </Text>
        ),
      },
      {
        id: 'estimatedHoursToGo',
        size: 150,
        accessorFn: (row) => row.estimatedHoursToGo,
        header: 'E@C',
        mantineTableHeadCellProps: {
          style: {
            backgroundColor: '#64ffc0',
          },
        },
        Cell: ({ cell, row }) => (
          <Text c={getValueColor(cell.getValue<number>(), 'default')}>
            {formatValue(cell.getValue<number>(), 0)}
          </Text>
        ),
      },
      {
        id: 'variance',
        size: 150,
        accessorFn: (row) => row.variance,
        header: 'Variance',
        Cell: ({ cell, row }) => (
          <Text c={getValueColor(cell.getValue<number>(), 'variance')}>
            {formatValue(cell.getValue<number>(), 0)}
          </Text>
        ),
      },
      {
        id: 'completionPercentageHours',
        size: 150,
        accessorFn: (row) => row.completionPercentageHours,
        header: '% Hrs Used',
        Cell: ({ cell }) => (
          <Text c={getValueColor(cell.getValue<number>(), 'default')}>
            {formatValue(cell.getValue<number>(), 2)}%
          </Text>
        ),
      },
      {
        id: 'completionPercentageQuantity',
        size: 150,
        accessorFn: (row) => row.completionPercentageQuantity,
        header: '% Qty Installed',
        Cell: ({ cell }) => (
          <Text c={getValueColor(cell.getValue<number>(), 'default')}>
            {formatValue(cell.getValue<number>(), 2)}%
          </Text>
        ),
      },
      {
        id: 'overallPerformance',
        size: 150,
        accessorFn: (row) => row.overallPerformance,
        header: 'Overall Performance',
        Cell: ({ cell }) => {
          const value = cell.getValue<number>();
          return (
            <Group>
              <ThemeIcon
                size="md"
                color={getValueColor(value, 'overall-performance')}
              >
                {value > 5 && <IconThumbUp size={20} />}
                {value >= -5 && value <= 5 && <IconTilde size={20} />}
                {value < -5 && <IconThumbDown size={20} />}
              </ThemeIcon>
              <Text c={getValueColor(value, 'overall-performance')}>
                {formatValue(value, 2)}%
              </Text>
            </Group>
          );
        },
      },
    ],
    [],
  );

  const tableState = useTableState({
    identifier: 'simple-tracker-table',
    columns: columns,
  });

  const tableExports = useTableExports<TrackerRowData>('tracker', (data) => {
    const flattenData = (items: TrackerRowData[], prefix = ''): any[] => {
      return items.flatMap((item) => {
        const current = {
          ['Name']: prefix + item.name,
          ['Estimated Hours']: item.estimatedHours,
          ['Estimated Quantity']: item.estimatedQuantity,
          ['Estimated Run Rate']: item.estimatedRunRate,
          ['Actual Hours']: item.actualHours,
          ['Actual Quantity']: item.actualQuantity,
          ['Actual Run Rate']: item.actualRunRate,
          ['Hours To Go']: item.hoursToGo,
          ['Estimated Hours To Go']: item.estimatedHoursToGo,
          ['Variance']: item.variance,
          ['Completion % (Hours)']: item.completionPercentageHours,
          ['Completion % (Quantity)']: item.completionPercentageQuantity,
          ['Overall Performance']: item.overallPerformance,
        };
        if (item.subRows && item.subRows.length > 0) {
          return [current, ...flattenData(item.subRows, prefix + '  ')];
        }
        return [current];
      });
    };
    return flattenData(data);
  });

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableExpanding: true,
      enableExpandAll: true,
      columns: columns,
      data: tableData,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      enableRowActions: false,
      enableClickToCopy: false,
      initialState: {
        expanded: {},
      },
    },
    getRowId: (row) => row.id,
    totalCount: tableData.length,
    isLoading: loading,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  if (loading) {
    return (
      <Paper>
        <Box style={{ textAlign: 'center', padding: '40px' }}>
          <Loader />
        </Box>
      </Paper>
    );
  }

  if (wbs.length === 0) {
    return (
      <Paper>
        <Text ta="center" py="xl">
          WBS not configured or no applicable charge codes found
        </Text>
      </Paper>
    );
  }

  return (
    <>
      {table}
      <TrackerLegend />
    </>
  );
};
